import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Eye, EyeOff, Star, Radio,
  ChevronLeft, Loader2, Newspaper
} from 'lucide-react';
import adminApi from '../api/adminApi';
import BilingualField from '../components/BilingualField';
import ImagePicker from '../components/ImagePicker';

const CATEGORIES = ['products', 'events', 'awards', 'industry', 'press'];

const EMPTY_FORM = {
  title_en: '', title_ar: '',
  summary_en: '', summary_ar: '',
  body_en: '', body_ar: '',
  category: 'press',
  cover_image_url: '',
  published: false,
  featured: false,
  show_in_ticker: false,
  tags: '',
  published_at: '',
};

const StatusBadge = ({ published }) => (
  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
    published
      ? 'bg-green-500/15 text-green-400'
      : 'bg-white/10 text-white/40'
  }`}>
    <span className={`w-1.5 h-1.5 rounded-full ${published ? 'bg-green-400' : 'bg-white/30'}`} />
    {published ? 'Published' : 'Draft'}
  </span>
);

const CategoryBadge = ({ category }) => (
  <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[rgba(212,175,55,0.15)] text-[#D4AF37] rounded-[2px]">
    {category}
  </span>
);

const NewsEditorPage = () => {
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' | 'editor'
  const [editing, setEditing] = useState(null); // article id or null for new
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/admin/news', { params: { page, limit: 15, status: statusFilter } });
      setArticles(r.data.articles || []);
      setTotal(r.data.total || 0);
      setPages(r.data.pages || 1);
    } catch {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setView('editor');
  };

  const openEdit = (article) => {
    setForm({
      title_en: article.title_en || '',
      title_ar: article.title_ar || '',
      summary_en: article.summary_en || '',
      summary_ar: article.summary_ar || '',
      body_en: article.body_en || '',
      body_ar: article.body_ar || '',
      category: article.category || 'press',
      cover_image_url: article.cover_image_url || '',
      published: article.published ?? false,
      featured: article.featured ?? false,
      show_in_ticker: article.show_in_ticker ?? false,
      tags: Array.isArray(article.tags) ? article.tags.join(', ') : '',
      published_at: article.published_at ? article.published_at.slice(0, 16) : '',
    });
    setEditing(article.id);
    setView('editor');
  };

  const handleSave = async (publishNow = false) => {
    if (!form.title_en.trim()) { toast.error('English title is required'); return; }
    if (!form.title_ar.trim()) { toast.error('Arabic title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        published: publishNow ? true : form.published,
        tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        published_at: form.published_at ? new Date(form.published_at).toISOString() : null,
      };
      if (editing) {
        await adminApi.put(`/admin/news/${editing}`, payload);
        toast.success('Article updated');
      } else {
        await adminApi.post('/admin/news', payload);
        toast.success(publishNow ? 'Article published' : 'Draft saved');
      }
      setView('list');
      fetchArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save article');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (article, field) => {
    const endpoint = field === 'published' ? 'publish' : field === 'featured' ? 'feature' : 'ticker';
    try {
      await adminApi.patch(`/admin/news/${article.id}/${endpoint}`);
      setArticles(prev => prev.map(a => a.id === article.id ? { ...a, [field]: !a[field] } : a));
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    try {
      await adminApi.delete(`/admin/news/${id}`);
      toast.success('Article deleted');
      setDeleteConfirm(null);
      fetchArticles();
    } catch {
      toast.error('Failed to delete');
    }
  };

  // ── EDITOR VIEW ──────────────────────────────────────────────────────────────
  if (view === 'editor') {
    return (
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setView('list')}
              className="p-2 text-white/50 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-display text-xl font-bold text-white">
                {editing ? 'Edit Article' : 'New Article'}
              </h1>
              <p className="text-white/40 text-xs">{editing ? `Editing article` : 'Create a new article'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold text-white/70 border border-white/20 hover:border-white/40 rounded-sm transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Draft'}
            </button>
            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold bg-[#D4AF37] text-black hover:bg-[#c9a832] rounded-sm transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Publish Now'}
            </button>
          </div>
        </div>

        {/* Cover Image */}
        <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-6">
          <ImagePicker
            label="Cover Image"
            value={form.cover_image_url}
            onChange={v => setForm(f => ({ ...f, cover_image_url: v }))}
          />
        </div>

        {/* Category & Settings */}
        <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-6 space-y-4">
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider">Category & Settings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-white/50 uppercase tracking-wider mb-2">Category</label>
              <select
                value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]"
              >
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/50 uppercase tracking-wider mb-2">Published At</label>
              <input
                type="datetime-local"
                value={form.published_at}
                onChange={e => setForm(f => ({ ...f, published_at: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="flex flex-wrap gap-4 pt-2">
            {[
              { key: 'published', label: 'Published' },
              { key: 'featured', label: 'Featured article' },
              { key: 'show_in_ticker', label: 'Show in ticker' },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2.5 cursor-pointer select-none">
                <div
                  onClick={() => setForm(f => ({ ...f, [key]: !f[key] }))}
                  className={`w-10 h-5 rounded-full transition-colors relative ${form[key] ? 'bg-[#D4AF37]' : 'bg-white/10'}`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form[key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-sm text-white/70">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Titles */}
        <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-6">
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">Title</h3>
          <BilingualField
            labelEn="Title (English)"
            labelAr="Title (Arabic)"
            valueEn={form.title_en}
            valueAr={form.title_ar}
            onChangeEn={v => setForm(f => ({ ...f, title_en: v }))}
            onChangeAr={v => setForm(f => ({ ...f, title_ar: v }))}
          />
        </div>

        {/* Summary */}
        <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-6">
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-4">Summary (shown on card)</h3>
          <BilingualField
            labelEn="Summary (English)"
            labelAr="Summary (Arabic)"
            valueEn={form.summary_en}
            valueAr={form.summary_ar}
            onChangeEn={v => setForm(f => ({ ...f, summary_en: v }))}
            onChangeAr={v => setForm(f => ({ ...f, summary_ar: v }))}
            multiline
            rows={3}
          />
        </div>

        {/* Body */}
        <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-6">
          <h3 className="text-sm font-semibold text-white/60 uppercase tracking-wider mb-1">Article Body</h3>
          <p className="text-white/30 text-xs mb-4">Separate paragraphs with a blank line (double newline)</p>
          <BilingualField
            labelEn="Body (English)"
            labelAr="Body (Arabic)"
            valueEn={form.body_en}
            valueAr={form.body_ar}
            onChangeEn={v => setForm(f => ({ ...f, body_en: v }))}
            onChangeAr={v => setForm(f => ({ ...f, body_ar: v }))}
            multiline
            rows={10}
          />
        </div>

        {/* Tags */}
        <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-6">
          <label className="block text-xs text-white/50 uppercase tracking-wider mb-2">Tags (comma-separated)</label>
          <input
            type="text"
            value={form.tags}
            onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
            placeholder="launch, reserve, limited-edition"
            className="w-full bg-[#0A0A0A] border border-[rgba(212,175,55,0.2)] text-white rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37] placeholder-white/20"
          />
        </div>

        {/* Footer actions (duplicate) */}
        <div className="flex justify-end gap-2 pb-4">
          <button onClick={() => setView('list')} className="px-4 py-2 text-sm text-white/50 hover:text-white transition-colors">
            Cancel
          </button>
          <button onClick={() => handleSave(false)} disabled={saving}
            className="px-4 py-2 text-sm font-semibold border border-white/20 hover:border-white/40 text-white/70 rounded-sm disabled:opacity-50 transition-colors">
            Save Draft
          </button>
          <button onClick={() => handleSave(true)} disabled={saving}
            className="px-4 py-2 text-sm font-semibold bg-[#D4AF37] text-black hover:bg-[#c9a832] rounded-sm disabled:opacity-50 transition-colors">
            Publish Now
          </button>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white mb-1">News Articles</h1>
          <p className="text-white/40 text-sm">{total} article{total !== 1 ? 's' : ''} total</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-black text-sm font-semibold rounded-sm hover:bg-[#c9a832] transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Article
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['all', 'published', 'draft'].map(s => (
          <button
            key={s}
            onClick={() => { setStatusFilter(s); setPage(1); }}
            className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm transition-colors ${
              statusFilter === s
                ? 'bg-[rgba(212,175,55,0.2)] text-[#D4AF37] border border-[rgba(212,175,55,0.4)]'
                : 'text-white/40 border border-transparent hover:text-white hover:border-white/20'
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Article list */}
      <div className="space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
          </div>
        ) : articles.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-white/30">
            <Newspaper className="w-12 h-12 mb-4 opacity-30" />
            <p className="font-display text-lg font-semibold mb-1">No Articles</p>
            <p className="text-sm">Click "New Article" to create your first post.</p>
          </div>
        ) : articles.map(article => (
          <div
            key={article.id}
            className="flex items-start gap-4 p-4 bg-[#111111] border border-[rgba(212,175,55,0.1)] rounded-sm hover:border-[rgba(212,175,55,0.3)] transition-colors"
          >
            {/* Thumbnail */}
            <div className="flex-shrink-0 w-14 h-14 rounded-sm overflow-hidden bg-[#1a1a1a] border border-white/5">
              {article.cover_image_url ? (
                <img src={article.cover_image_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/10">
                  <Newspaper className="w-5 h-5" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <CategoryBadge category={article.category} />
                <StatusBadge published={article.published} />
                {article.featured && (
                  <span className="text-[10px] text-[#D4AF37] border border-[rgba(212,175,55,0.3)] px-1.5 py-0.5 rounded-full">Featured</span>
                )}
                {article.show_in_ticker && (
                  <span className="text-[10px] text-white/40 border border-white/10 px-1.5 py-0.5 rounded-full">In Ticker</span>
                )}
              </div>
              <p className="text-white font-medium text-sm truncate">{article.title_en}</p>
              <p className="text-white/30 text-xs mt-0.5">
                {article.published_at
                  ? new Date(article.published_at).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', year: 'numeric' })
                  : 'No date'}
              </p>
            </div>

            {/* Actions */}
            <div className="flex-shrink-0 flex items-center gap-1">
              {/* Toggle published */}
              <button
                onClick={() => handleToggle(article, 'published')}
                title={article.published ? 'Unpublish' : 'Publish'}
                className={`p-1.5 rounded-sm transition-colors ${
                  article.published ? 'text-green-400 hover:bg-green-400/10' : 'text-white/30 hover:text-white hover:bg-white/5'
                }`}
              >
                {article.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              {/* Toggle featured */}
              <button
                onClick={() => handleToggle(article, 'featured')}
                title={article.featured ? 'Unfeature' : 'Feature'}
                className={`p-1.5 rounded-sm transition-colors ${
                  article.featured ? 'text-[#D4AF37] hover:bg-[rgba(212,175,55,0.1)]' : 'text-white/30 hover:text-white hover:bg-white/5'
                }`}
              >
                <Star className="w-4 h-4" />
              </button>
              {/* Toggle ticker */}
              <button
                onClick={() => handleToggle(article, 'show_in_ticker')}
                title={article.show_in_ticker ? 'Remove from ticker' : 'Add to ticker'}
                className={`p-1.5 rounded-sm transition-colors ${
                  article.show_in_ticker ? 'text-blue-400 hover:bg-blue-400/10' : 'text-white/30 hover:text-white hover:bg-white/5'
                }`}
              >
                <Radio className="w-4 h-4" />
              </button>
              {/* Edit */}
              <button
                onClick={() => openEdit(article)}
                className="p-1.5 text-white/40 hover:text-white hover:bg-white/5 rounded-sm transition-colors"
              >
                <Pencil className="w-4 h-4" />
              </button>
              {/* Delete */}
              {deleteConfirm === article.id ? (
                <div className="flex items-center gap-1 ml-1">
                  <span className="text-xs text-red-400">Delete?</span>
                  <button onClick={() => handleDelete(article.id)} className="text-xs text-red-400 hover:text-red-300 font-semibold px-1">Yes</button>
                  <button onClick={() => setDeleteConfirm(null)} className="text-xs text-white/40 hover:text-white font-semibold px-1">No</button>
                </div>
              ) : (
                <button
                  onClick={() => setDeleteConfirm(article.id)}
                  className="p-1.5 text-white/30 hover:text-red-400 hover:bg-red-400/10 rounded-sm transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm text-white/50 hover:text-white border border-white/10 hover:border-white/30 rounded-sm disabled:opacity-30 transition-colors"
          >
            ← Prev
          </button>
          <span className="text-white/40 text-sm">Page {page} of {pages}</span>
          <button
            onClick={() => setPage(p => Math.min(pages, p + 1))}
            disabled={page === pages}
            className="px-3 py-1.5 text-sm text-white/50 hover:text-white border border-white/10 hover:border-white/30 rounded-sm disabled:opacity-30 transition-colors"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsEditorPage;
